import React, { useEffect, useMemo } from 'react';
import { useCustomForm } from '../../hooks/useCustomForm';
import { Permission } from '../../types/permission';
import { MenuOption } from '../../types/menuOption';

interface PermissionModalProps {
    permission: Permission | null;
    menuOptions: MenuOption[];
    onClose: () => void;
    onSave: (permission: Permission) => void;
}

const PermissionModal: React.FC<PermissionModalProps> = ({ permission, menuOptions, onClose, onSave }) => {

    //Define los valores por defecto, ya sea para crear o editar
    const defaultValues = useMemo<Permission> (()=> {
        return permission || {
            permission_name: '',
            menu_id: 0,
            description: '',
            is_active: true,
        };
    }, [permission]);


    //Funcion a ejecutar al enviar el formulario
    const onSubmit = (data: Permission) => {
        //console.log('Permiso a crear/editar:', data);
        const updatedPermission = permission && permission.permission_id
            ? { ...permission, ...data }
            : data;
        onSave(updatedPermission);
    };

    //Utiliza el Hook personalizado
    const { register, errors, isValid, submitHandler, reset } = useCustomForm<Permission>({
        defaultValues,
        onSubmit,
    });


    useEffect(() => {
        reset(defaultValues);
    }, [permission, reset, defaultValues]);

    return (
        <div className="modal show d-block" tabIndex={-1} role='dialog'>
            <div className="modal-dialog modal-dialog-centered" role='document'>
                <div className="modal-content shadow-lg bg-light">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {permission ? `Editar Permiso (${permission.permission_id})` : 'Crear Permiso'} 
                        </h5>
                        <button type='button' className="btn-close" onClick={onClose} aria-label='Close'></button>
                    </div>
                    <form action="" onSubmit={submitHandler}>
                        <div className="modal-body">
                            <div className="mb-3">
                                <label htmlFor="permission_name" className="form-label">
                                    Nombre del Permiso
                                </label>
                                <input
                                    id='permission_name'
                                    type="text"
                                    className={`form-control ${errors.permission_name ? 'is-invalid' : ''}`}
                                    {...register('permission_name', { required: 'El nombre es requerido' })}
                                />
                                {errors.permission_name && (
                                    <div className="invalid-feedback">{errors.permission_name.message}</div>
                                )}
                            </div>
                            <div className="mb-3">
                                <label htmlFor="menu_id" className="form-label">Menú</label>
                                <select id="menu_id"
                                    className={`form-select ${errors.menu_id ? 'is_invalid' : ''}`}
                                    {...register('menu_id', { required: 'El menú es requerido', valueAsNumber: true })}
                                >
                                    <option value="" className='text-muted'>Seleccione un menú</option>
                                    {menuOptions.map((option) => (
                                        option.is_active && (
                                            <option key={option.menu_id} value={option.menu_id}>
                                                {option.menu_name}
                                            </option>
                                        )
                                    ))}
                                </select>
                                {errors.menu_id && (
                                    <div className="invalid-feedback">{errors.menu_id.message}</div>
                                )}
                            </div>
                            <div className="mb-3">
                                <label htmlFor="description" className="form-label">
                                    Descripción
                                </label>
                                <textarea
                                    id="description"
                                    className="form-control"
                                    {...register('description')}
                                ></textarea>
                            </div>
                            <div className="form-check">
                                <input
                                    id='is_active'
                                    type="checkbox"
                                    className="form-check-input"
                                    {...register('is_active')}
                                />
                                <label htmlFor="is_active" className="form-check-label">
                                    Activo
                                </label>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type='button' className="btn btn-secondary" onClick={onClose}>
                                Cancelar
                            </button>
                            <button type='submit' className="btn btn-primary" disabled={!isValid}>
                                Guardar
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PermissionModal;



